import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowRightLeft,
  Building2,
  Check,
  X,
  Clock,
  Loader2,
  ArrowRight,
  XCircle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TransferRequest {
  id: string;
  status: string;
  reason: string | null;
  rejection_reason: string | null;
  created_at: string;
  from_agency: {
    name: string;
  };
  to_agency: {
    name: string;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<any>; description: string }> = {
  pending_release: { 
    label: 'Awaiting Release', 
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', 
    icon: Clock,
    description: 'Your current agency is reviewing your transfer request.'
  },
  pending_acceptance: { 
    label: 'Awaiting Acceptance', 
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', 
    icon: Clock,
    description: 'Your current agency has approved. Waiting for the new agency to accept.'
  },
  accepted: { 
    label: 'Transfer Complete', 
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', 
    icon: Check,
    description: 'Your transfer has been approved! You can now register with your new agency.'
  },
  rejected_by_source: { 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', 
    icon: X,
    description: 'Your current agency has declined the transfer request.'
  },
  rejected_by_target: { 
    label: 'Rejected', 
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', 
    icon: X,
    description: 'The new agency has declined to accept your transfer.'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', 
    icon: XCircle,
    description: 'You cancelled this transfer request.'
  },
};

interface TransferRequestStatusProps {
  patientId: string;
  onRequestTransfer: () => void;
}

export function TransferRequestStatus({ patientId, onRequestTransfer }: TransferRequestStatusProps) {
  const { toast } = useToast();
  const [request, setRequest] = useState<TransferRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    loadActiveRequest();
  }, [patientId]);

  const loadActiveRequest = async () => {
    try {
      const { data, error } = await supabase
        .from('patient_transfer_requests')
        .select(`
          id,
          status,
          reason,
          rejection_reason,
          created_at,
          from_agency:agencies!patient_transfer_requests_from_agency_id_fkey(name),
          to_agency:agencies!patient_transfer_requests_to_agency_id_fkey(name)
        `)
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setRequest(data as unknown as TransferRequest);
    } catch (error) {
      console.error('Error loading transfer request:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!request) return;

    setCancelling(true);
    try {
      const { error } = await supabase
        .from('patient_transfer_requests')
        .update({ status: 'cancelled' })
        .eq('id', request.id);

      if (error) throw error;

      toast({
        title: 'Transfer Cancelled',
        description: 'Your transfer request has been cancelled.',
      });

      loadActiveRequest();
    } catch (error) {
      console.error('Error cancelling request:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // No active request or completed/cancelled - show option to request
  if (!request || ['accepted', 'rejected_by_source', 'rejected_by_target', 'cancelled'].includes(request.status)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5" />
            Agency Transfer
          </CardTitle>
          <CardDescription>
            Request to transfer your care to a different agency
          </CardDescription>
        </CardHeader>
        <CardContent>
          {request && request.status === 'accepted' && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">
                <Check className="inline h-4 w-4 mr-1" />
                Your previous transfer to <strong>{request.to_agency.name}</strong> was completed. 
                You can register with them now.
              </p>
            </div>
          )}
          <Button onClick={onRequestTransfer}>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Request Transfer
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Active request - show status
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending_release;
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5" />
            Transfer Request
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Badge className={statusConfig.color}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusConfig.label}
          </Badge>

          <p className="text-sm text-muted-foreground">
            {statusConfig.description}
          </p>

          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1 px-3 py-1.5 bg-muted rounded-lg">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span>{request.from_agency.name}</span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-1 px-3 py-1.5 bg-muted rounded-lg">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span>{request.to_agency.name}</span>
            </div>
          </div>

          {request.reason && (
            <p className="text-sm bg-muted/50 rounded-lg p-3">
              <span className="font-medium">Your reason:</span> {request.reason}
            </p>
          )}

          {request.rejection_reason && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg p-3">
              <span className="font-medium">Rejection reason:</span> {request.rejection_reason}
            </p>
          )}

          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-muted-foreground">
              Requested {formatDate(request.created_at)}
            </span>
            {request.status === 'pending_release' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelling}
              >
                {cancelling ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-1" />
                    Cancel Request
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Transfer Request?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel your transfer request to {request.to_agency.name}? 
              You can submit a new request later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>Keep Request</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling...' : 'Cancel Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
